<template>
  <div class="create-exam-view exam-view-container">
    <div class="mb-6">
      <div class="flex items-center mb-4">
        <button
          @click="$emit('back')"
          class="flex items-center text-secondary-text dark:text-secondary-dark-text hover:text-primary-text dark:hover:text-primary-dark-text transition-colors mr-4"
        >
          <ArrowLeft class="w-5 h-5 mr-2" />
          Back to Exams
        </button>
      </div>
      <h1 class="text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text mb-2">
        Create New Exam
      </h1>
      <p class="text-secondary-text dark:text-secondary-dark-text">
        Design and create new examination content with custom questions and settings.
      </p>
    </div>

    <!-- Create Exam Form -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <!-- Main Form -->
      <div class="xl:col-span-2 space-y-6">
        <!-- Basic Information -->
        <div class="card">
          <div class="card-header">
            <div class="flex items-center">
              <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                <FileText class="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 class="text-lg font-semibold text-primary-text dark:text-primary-dark-text">
                Basic Information
              </h3>
            </div>
          </div>
          <div class="card-body space-y-4">
            <div>
              <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                Exam Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="newExam.exam_name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                placeholder="Enter exam name"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                Module <span class="text-red-500">*</span>
              </label>
              <select
                v-model.number="newExam.module_id"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
              >
                <option :value="0">Select a module</option>
                <option v-for="module in modules" :key="module.module_id" :value="module.module_id">
                  {{ module.name }}
                </option>
              </select>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                  Passing Rate (%) <span class="text-red-500">*</span>
                </label>
                <input
                  v-model.number="newExam.passing_rate"
                  type="number"
                  min="0"
                  max="100"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="70"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                  Time Limit (minutes)
                </label>
                <input
                  v-model.number="newExam.time_limit"
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  @input="handleTimeLimitInput"
                  @keypress="preventDecimalInput"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="No time limit"
                />
                <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                  Leave empty for no time limit (max: 1440 minutes)
                </p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                  Max Attempts
                </label>
                <input
                  v-model.number="newExam.total_attempts"
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                  @input="handleTotalAttemptsInput"
                  @keypress="preventDecimalInput"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="Unlimited"
                />
                <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                  Leave empty for unlimited (max: 10 attempts)
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Questions Section -->
        <div class="card">
          <div class="card-header">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                  <HelpCircle class="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-primary-text dark:text-primary-dark-text">
                    Questions ({{ newExam.exam_content.questions.length }}/10)
                  </h3>
                  <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                    Each module must contain exactly 10 questions
                  </p>
                </div>
              </div>
              <button
                @click="addQuestion"
                :disabled="newExam.exam_content.questions.length >= 10"
                :class="[
                  'btn btn-sm flex items-center gap-2',
                  newExam.exam_content.questions.length >= 10 
                    ? 'btn-secondary opacity-50 cursor-not-allowed' 
                    : 'btn-primary'
                ]"
              >
                <Plus class="w-4 h-4" />
                Add Question
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="newExam.exam_content.questions.length === 0" class="text-center py-8">
              <HelpCircle class="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 class="text-lg font-medium text-primary-text dark:text-primary-dark-text mb-2">
                No questions added yet
              </h3>
              <p class="text-secondary-text dark:text-secondary-dark-text mb-4">
                Start building your exam by adding exactly 10 questions.
              </p>
              <button
                @click="addQuestion"
                class="btn btn-primary"
              >
                Add Your First Question
              </button>
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="(question, index) in newExam.exam_content.questions"
                :key="question.id"
                :class="[
                  'border rounded-lg p-4',
                  incompleteQuestions[index]?.isComplete 
                    ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/10' 
                    : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                ]"
              >
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <h4 class="text-sm font-medium text-primary-text dark:text-primary-dark-text">
                      Question {{ index + 1 }}
                    </h4>
                    <span v-if="incompleteQuestions[index]?.isComplete" class="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                      Complete
                    </span>
                    <span v-else class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                      Incomplete
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      @click="removeQuestion(index)"
                      :disabled="newExam.exam_content.questions.length <= 10"
                      :class="[
                        'transition-colors',
                        newExam.exam_content.questions.length <= 10
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-red-500 hover:text-red-700'
                      ]"
                      :title="newExam.exam_content.questions.length <= 10 ? 'Cannot remove questions - exam must have exactly 10 questions' : 'Remove question'"
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <!-- Show validation issues if question is incomplete -->
                <div v-if="!incompleteQuestions[index]?.isComplete" class="mb-3 p-2 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
                  <p class="text-red-700 dark:text-red-300 font-medium mb-1">Required Fields:</p>
                  <ul class="text-red-600 dark:text-red-400 text-xs space-y-1">
                    <li v-for="issue in incompleteQuestions[index]?.issues" :key="issue" class="flex items-center gap-1">
                      <span class="w-1 h-1 bg-red-500 rounded-full"></span>
                      {{ issue }}
                    </li>
                  </ul>
                </div>
                
                <div class="space-y-3">
                  <div>
                    <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-1">
                      Question Text *
                    </label>
                    <textarea
                      v-model="question.question"
                      rows="2"
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                      placeholder="Enter your question here..."
                    ></textarea>
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-1">
                      Question Type *
                    </label>
                    <select
                      v-model="question.type"
                      @change="updateQuestionType(question)"
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="true_false">True/False</option>
                      <option value="essay">Essay</option>
                    </select>
                  </div>

                  <!-- Multiple Choice Options -->
                  <div v-if="question.type === 'multiple_choice'" class="space-y-2">
                    <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-1">
                      Options *
                    </label>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Fill options in order (A, then B, then C, etc.). Empty options with filled options after them will show validation errors.
                    </p>
                    <div v-for="(option, optIndex) in question.options" :key="optIndex" class="flex items-center gap-2">
                      <span class="text-sm font-medium text-gray-600 dark:text-gray-400 w-6">
                        {{ String.fromCharCode(65 + optIndex) }}.
                      </span>
                      <input
                        v-model="option.text"
                        type="text"
                        :class="[
                          'flex-1 px-3 py-2 border rounded-lg transition duration-200',
                          'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                          'focus:ring-2 focus:ring-primary focus:border-primary',
                          getOptionValidationClass(question, optIndex)
                        ]"
                        :placeholder="`Option ${String.fromCharCode(65 + optIndex)}`"
                      />
                      <button
                        v-if="question.options && question.options.length > 2"
                        @click="removeOption(question, optIndex)"
                        class="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X class="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      v-if="question.options && question.options.length < 6"
                      @click="addOption(question)"
                      class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                      <Plus class="w-3 h-3" />
                      Add Option
                    </button>
                  </div>

                  <!-- Answer Key -->
                  <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <label class="block text-sm font-medium text-primary-text dark:text-primary-dark-text mb-2">
                      Correct Answer *
                    </label>
                    
                    <div v-if="question.type === 'multiple_choice'">
                      <select
                        v-model="newExam.answer_key[question.id]"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                      >
                        <option value="">Select correct option</option>
                        <option 
                          v-for="option in question.options?.filter(opt => opt.text && opt.text.trim())" 
                          :key="option.id" 
                          :value="option.id"
                        >
                          {{ option.id.toUpperCase() }}. {{ option.text }}
                        </option>
                      </select>
                    </div>
                    
                    <div v-else-if="question.type === 'true_false'">
                      <select
                        v-model="newExam.answer_key[question.id]"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                      >
                        <option value="">Select answer</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                    
                    <div v-else-if="question.type === 'essay'">
                      <KeywordInput
                        :model-value="getEssayKeywords(question.id)"
                        @update:model-value="updateEssayKeywords(question.id, $event)"
                        placeholder="Enter keywords or key points for this essay question..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="newExam.exam_content.questions.length >= 3" class="text-center mt-4">
                <button
                  @click="scrollToTop"
                  type="button"
                  class="inline-flex items-center text-sm text-primary-text dark:text-primary-dark-text hover:underline"
                >
                  <ArrowUp class="w-4 h-4 mr-2" />
                  Back to top
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <div class="sticky-sidebar">
        <!-- Preview Card -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-primary-text dark:text-primary-dark-text">
              Exam Preview
            </h3>
          </div>
          <div class="card-body space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Module</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ newExam.module_id > 0 ? 'Selected' : 'Not selected' }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Questions</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ newExam.exam_content.questions.length }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Complete Questions</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ incompleteQuestions.filter(q => q.isComplete).length }} / {{ incompleteQuestions.length }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Passing Rate</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ newExam.passing_rate || 0 }}%
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Time Limit</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ newExam.time_limit ? `${newExam.time_limit} min` : 'No limit' }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Max Attempts</span>
              <span class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ newExam.total_attempts ? `${newExam.total_attempts}` : 'Unlimited' }}
              </span>
            </div>
            
            <!-- Validation Status -->
            <div class="pt-3 border-t border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-primary-text dark:text-primary-dark-text">Status</span>
                <span v-if="canSaveExam" class="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                  Ready to Save
                </span>
                <span v-else class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                  Not Ready
                </span>
              </div>
              <div v-if="!canSaveExam" class="text-xs text-red-600 dark:text-red-400 space-y-1">
                <p v-if="!newExam.exam_name || !newExam.exam_name.trim()">• Enter exam name</p>
                <p v-if="!newExam.passing_rate || newExam.passing_rate <= 0 || newExam.passing_rate > 100">• Set valid passing rate (1-100%)</p>
                <p v-if="newExam.module_id <= 0">• Select a module</p>
                <p v-if="newExam.exam_content.questions.length !== 10">• Add exactly 10 questions (currently {{ newExam.exam_content.questions.length }})</p>
                <p v-else-if="incompleteQuestions.filter(q => q.isComplete).length < 10">• Complete all 10 questions</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-2 card">
          <div class="card-body space-y-3">
            <button
              @click="saveExam"
              :disabled="!canSaveExam || loading"
              class="w-full btn btn-primary"
            >
              <Save class="w-4 h-4 mr-2" />
              Save Exam
            </button>
            <button
              @click="saveDraftExam"
              :disabled="!canSaveExam || loading"
              class="w-full btn btn-secondary"
            >
              <FileText class="w-4 h-4 mr-2" />
              Save as Draft
            </button>
            <button
              @click="$emit('back')"
              class="w-full btn btn-outline"
            >
              Cancel
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Incomplete Questions Confirmation Modal -->
    <IncompleteQuestionsModal
      :is-visible="showIncompleteModal"
      :incomplete-questions="modalQuestionsData"
      @close="showIncompleteModal = false"
      @confirm="proceedWithSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useToastService } from '@/services/toast'
import { examService } from '@/services/examService'
import { ModulesService } from '@/services/modules'
import type { Question, CreateExamPayload } from '@/services/examService'
import type { Module } from '@/services/modules'
import IncompleteQuestionsModal from '@/components/modals/IncompleteQuestionsModal.vue'
import KeywordInput from '@/components/forms/KeywordInput.vue'
import {
  Plus,
  FileText,
  X,
  ArrowLeft,
  HelpCircle,
  Save,
  Trash2,
  ArrowUp
} from 'lucide-vue-next'

// Props and Emits
const emit = defineEmits<{
  back: []
  examCreated: []
}>()

// Page title
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Empathy Health Services, Inc.')
useTitle(`Create New Exam - ${appTitle.value}`)

// Services
const toastService = useToastService()

// Helper function to maintain backward compatibility
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  if (type === 'success') {
    toastService.success(message)
  } else if (type === 'error') {
    toastService.error(message)
  } else {
    toastService.success(message) // Use success for info since the service may not have info
  }
}

// State
const loading = ref(false)
const modules = ref<Module[]>([])
const showIncompleteModal = ref(false)
const pendingSaveType = ref<'exam' | 'draft'>('exam')

// New exam form with proper typing
const newExam = ref<CreateExamPayload>({
  exam_name: '',
  module_id: 0,
  exam_content: {
    questions: [] as Question[]
  },
  answer_key: {} as Record<string, any>,
  passing_rate: 70,
  time_limit: null,
  total_attempts: 3,
  exam_status: 1
})

// Question ID counter
let questionIdCounter = 1

// Helper method for essay keywords
const getEssayKeywords = (questionId: number): string[] => {
  if (!newExam.value.answer_key[questionId]) {
    return []
  }
  return newExam.value.answer_key[questionId] as string[]
}

const updateEssayKeywords = (questionId: number, keywords: string[]) => {
  newExam.value.answer_key[questionId] = keywords
}

// Input validation functions
const preventDecimalInput = (event: KeyboardEvent) => {
  // Prevent decimal point and minus sign
  if (event.key === '.' || event.key === ',' || event.key === '-' || event.key === 'e' || event.key === 'E') {
    event.preventDefault()
  }
}

const handleTimeLimitInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value
  
  // If empty, set to null (unlimited)
  if (value === '' || value === null) {
    newExam.value.time_limit = null
    return
  }
  
  // Remove any non-digit characters
  value = value.replace(/[^\d]/g, '')
  
  // Convert to number
  let numValue = parseInt(value, 10)
  
  // If NaN, set to null
  if (isNaN(numValue)) {
    newExam.value.time_limit = null
    target.value = ''
    return
  }
  
  // Enforce min and max
  if (numValue < 1) {
    numValue = 1
  } else if (numValue > 1440) {
    numValue = 1440
  }
  
  newExam.value.time_limit = numValue
  target.value = numValue.toString()
}

const handleTotalAttemptsInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value
  
  // If empty, set to null (unlimited)
  if (value === '' || value === null) {
    newExam.value.total_attempts = null
    return
  }
  
  // Remove any non-digit characters
  value = value.replace(/[^\d]/g, '')
  
  // Convert to number
  let numValue = parseInt(value, 10)
  
  // If NaN, set to null
  if (isNaN(numValue)) {
    newExam.value.total_attempts = null
    target.value = ''
    return
  }
  
  // Enforce min and max
  if (numValue < 1) {
    numValue = 1
  } else if (numValue > 10) {
    numValue = 10
  }
  
  newExam.value.total_attempts = numValue
  target.value = numValue.toString()
}

// Computed properties
const canSaveExam = computed(() => {
  // Check if exam name is filled
  if (!newExam.value.exam_name || !newExam.value.exam_name.trim()) {
    return false
  }

  // Check if passing rate is set (should be between 1 and 100)
  if (!newExam.value.passing_rate || newExam.value.passing_rate <= 0 || newExam.value.passing_rate > 100) {
    return false
  }

  // Check if module is selected
  if (newExam.value.module_id <= 0) {
    return false
  }

  // Check if there are exactly 10 questions
  if (newExam.value.exam_content.questions.length !== 10) {
    return false
  }

  // Check if all 10 questions are properly filled
  const validQuestionsCount = newExam.value.exam_content.questions.filter((question: Question) => {
    // Question text must be filled
    if (!question.question.trim()) {
      return false
    }

    // Check based on question type
    if (question.type === 'multiple_choice') {
      // Must have at least 2 options with text, and a correct answer selected
      const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
      const hasCorrectAnswer = newExam.value.answer_key[question.id]
      return validOptions.length >= 2 && hasCorrectAnswer
    } else if (question.type === 'true_false') {
      // Must have a correct answer selected
      return newExam.value.answer_key[question.id] !== undefined && newExam.value.answer_key[question.id] !== ''
    } else if (question.type === 'essay') {
      // Must have at least one keyword
      const keywords = newExam.value.answer_key[question.id] || []
      return keywords.length > 0
    }

    return false
  }).length

  // All 10 questions must be valid
  return validQuestionsCount === 10
})

// Helper computed to track which questions are incomplete
const incompleteQuestions = computed(() => {
  return newExam.value.exam_content.questions.map((question: Question, index: number) => {
    const issues: string[] = []
    
    // Check if question text is filled
    if (!question.question.trim()) {
      issues.push('Question text is required')
    }
    
    // Check based on question type
    if (question.type === 'multiple_choice') {
      const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
      
      // Check if options are filled in order (no gaps)
      if (question.options) {
        for (let i = 0; i < question.options.length; i++) {
          const currentOption = question.options[i]
          const isEmpty = !currentOption.text || !currentOption.text.trim()
          
          if (isEmpty) {
            // Check if there are filled options after this empty one
            const hasFilledAfter = question.options.slice(i + 1).some(opt => opt.text && opt.text.trim())
            if (hasFilledAfter) {
              issues.push(`Option ${String.fromCharCode(65 + i)} must be filled before later options`)
            }
          }
        }
      }
      
      if (validOptions.length < 2) {
        issues.push('At least 2 options must be filled')
      }
      if (!newExam.value.answer_key[question.id]) {
        issues.push('Correct answer must be selected')
      }
    } else if (question.type === 'true_false') {
      if (newExam.value.answer_key[question.id] === undefined || newExam.value.answer_key[question.id] === '') {
        issues.push('Correct answer must be selected')
      }
    } else if (question.type === 'essay') {
      const keywords = newExam.value.answer_key[question.id] || []
      if (keywords.length === 0) {
        issues.push('At least one keyword is required')
      }
    }
    
    return {
      index,
      isComplete: issues.length === 0,
      issues
    }
  })
})

// Helper function to check if a question is complete
const isQuestionComplete = (question: Question): boolean => {
  // Check if question text is filled
  if (!question.question.trim()) {
    return false
  }
  
  // Check based on question type
  if (question.type === 'multiple_choice') {
    const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
    
    // Check if options are filled in order (no gaps)
    if (question.options) {
      for (let i = 0; i < question.options.length; i++) {
        const currentOption = question.options[i]
        const isEmpty = !currentOption.text || !currentOption.text.trim()
        
        if (isEmpty) {
          // Check if there are filled options after this empty one
          const hasFilledAfter = question.options.slice(i + 1).some(opt => opt.text && opt.text.trim())
          if (hasFilledAfter) {
            return false // Has gaps in options
          }
        }
      }
    }
    
    if (validOptions.length < 2) {
      return false
    }
    if (!newExam.value.answer_key[question.id]) {
      return false
    }
  } else if (question.type === 'true_false') {
    if (newExam.value.answer_key[question.id] === undefined || newExam.value.answer_key[question.id] === '') {
      return false
    }
  } else if (question.type === 'essay') {
    const keywords = newExam.value.answer_key[question.id] || []
    if (keywords.length === 0) {
      return false
    }
  }
  
  return true
}

// Scroll helper to go back to top of this view
const scrollToTop = () => {
  const el = document.querySelector('.create-exam-view') as HTMLElement | null
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Computed property for modal data - reuses incompleteQuestions logic
const modalQuestionsData = computed(() => {
  return incompleteQuestions.value.map((questionData) => ({
    index: questionData.index,
    question: newExam.value.exam_content.questions[questionData.index]?.question || '',
    isComplete: questionData.isComplete,
    issues: questionData.issues
  }))
})

// Methods
const fetchModules = async () => {
  try {
    const response = await ModulesService.getAvailableModulesForExams()
    modules.value = response || []
  } catch (err: any) {
    console.error('Error fetching available modules:', err)
    showToast('Failed to load available modules', 'error')
  }
}

// Exam form methods
const addQuestion = () => {
  // Check if we already have 10 questions
  if (newExam.value.exam_content.questions.length >= 10) {
    showToast('Cannot add more than 10 questions. Each exam must have exactly 10 questions.', 'error')
    return
  }
  
  const newQuestion: Question = {
    id: questionIdCounter++,
    question: '',
    type: 'multiple_choice',
    options: [
      { id: 'a', text: '' },
      { id: 'b', text: '' },
      { id: 'c', text: '' },
      { id: 'd', text: '' }
    ]
  }
  newExam.value.exam_content.questions.push(newQuestion)
}

const removeQuestion = (index: number) => {
  // Prevent removing questions if we have 10 or fewer
  if (newExam.value.exam_content.questions.length <= 10) {
    showToast('Cannot remove questions. Each exam must have exactly 10 questions.', 'error')
    return
  }
  
  const question = newExam.value.exam_content.questions[index]
  delete newExam.value.answer_key[question.id]
  newExam.value.exam_content.questions.splice(index, 1)
}

const updateQuestionType = (question: Question) => {
  // Reset answer key when question type changes
  delete (newExam.value.answer_key as any)[question.id]
  
  // Initialize answer key based on type
  if (question.type === 'essay') {
    newExam.value.answer_key[question.id] = []
  }
  
  // Update options based on type
  if (question.type === 'multiple_choice') {
    question.options = [
      { id: 'a', text: '' },
      { id: 'b', text: '' },
      { id: 'c', text: '' },
      { id: 'd', text: '' }
    ]
  } else {
    delete (question as any).options
  }
}

const addOption = (question: Question) => {
  if (question.options) {
    // Check if all current options are filled before adding a new one
    const hasEmptyOptions = question.options.some(option => !option.text || !option.text.trim())
    
    if (hasEmptyOptions) {
      showToast('Please fill all current options before adding a new one', 'info')
      return
    }
    
    const nextLetter = String.fromCharCode(97 + question.options.length) // a, b, c, d, e, f
    question.options.push({
      id: nextLetter,
      text: ''
    })
  }
}

// Helper function to get validation classes for options
const getOptionValidationClass = (question: Question, optIndex: number) => {
  if (!question.options) return 'border-gray-300 dark:border-gray-600'
  
  const option = question.options[optIndex]
  const isEmpty = !option.text || !option.text.trim()
  
  if (isEmpty) {
    // Check if there are filled options after this empty one
    const hasFilledAfter = question.options.slice(optIndex + 1).some(opt => opt.text && opt.text.trim())
    if (hasFilledAfter) {
      return 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/10'
    }
    return 'border-gray-300 dark:border-gray-600'
  }
  
  return 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/10'
}

const removeOption = (question: Question, index: number) => {
  if (question.options) {
    question.options.splice(index, 1)
    // Update option IDs to maintain a, b, c, d sequence
    question.options.forEach((option: { id: string; text: string }, idx: number) => {
      option.id = String.fromCharCode(97 + idx)
    })
  }
}

// Helper function to clean up exam data before saving
const cleanExamData = (examData: CreateExamPayload): CreateExamPayload => {
  const cleanedData = JSON.parse(JSON.stringify(examData)) // Deep clone
  
  // Filter out incomplete questions first, then clean up the remaining complete questions
  cleanedData.exam_content.questions = cleanedData.exam_content.questions
    .filter((question: Question) => isQuestionComplete(question))
    .map((question: Question) => {
      const cleanedQuestion = { ...question }
      
      // For multiple choice questions, filter out empty options
      if (question.type === 'multiple_choice' && question.options) {
        cleanedQuestion.options = question.options.filter(option => 
          option.text && option.text.trim() !== ''
        )
        
        // Re-assign option IDs to maintain sequence (a, b, c, etc.)
        cleanedQuestion.options.forEach((option: { id: string; text: string }, idx: number) => {
          option.id = String.fromCharCode(97 + idx) // a, b, c, d, e, f
        })
      }
      
      return cleanedQuestion
    })
  
  return cleanedData
}

const saveExam = async () => {
  // Check if exam name is filled
  if (!newExam.value.exam_name || !newExam.value.exam_name.trim()) {
    showToast('Please enter an exam name before saving', 'error')
    return
  }

  // Check if passing rate is set
  if (!newExam.value.passing_rate || newExam.value.passing_rate <= 0 || newExam.value.passing_rate > 100) {
    showToast('Please set a valid passing rate (1-100%)', 'error')
    return
  }

  // Check if module is selected
  if (newExam.value.module_id <= 0) {
    showToast('Please select a module before saving', 'error')
    return
  }

  // Check if there are any questions
  if (newExam.value.exam_content.questions.length === 0) {
    showToast('Please add at least one question before saving', 'error')
    return
  }

  // Check if there are any complete questions
  const hasCompleteQuestion = incompleteQuestions.value.some(q => q.isComplete)
  if (!hasCompleteQuestion) {
    showToast('Please complete at least one question before saving', 'error')
    return
  }

  // Check if there are incomplete questions
  const hasIncompleteQuestions = incompleteQuestions.value.some(q => !q.isComplete)
  if (hasIncompleteQuestions) {
    pendingSaveType.value = 'exam'
    showIncompleteModal.value = true
    return
  }

  // If all questions are complete, proceed directly
  await performSave('exam')
}

const saveDraftExam = async () => {
  // Check if exam name is filled
  if (!newExam.value.exam_name || !newExam.value.exam_name.trim()) {
    showToast('Please enter an exam name before saving', 'error')
    return
  }

  // Check if passing rate is set
  if (!newExam.value.passing_rate || newExam.value.passing_rate <= 0 || newExam.value.passing_rate > 100) {
    showToast('Please set a valid passing rate (1-100%)', 'error')
    return
  }

  // Check if module is selected
  if (newExam.value.module_id <= 0) {
    showToast('Please select a module before saving', 'error')
    return
  }

  // Check if there are any questions
  if (newExam.value.exam_content.questions.length === 0) {
    showToast('Please add at least one question before saving', 'error')
    return
  }

  // Check if there are any complete questions
  const hasCompleteQuestion = incompleteQuestions.value.some(q => q.isComplete)
  if (!hasCompleteQuestion) {
    showToast('Please complete at least one question before saving', 'error')
    return
  }

  // Check if there are incomplete questions
  const hasIncompleteQuestions = incompleteQuestions.value.some(q => !q.isComplete)
  if (hasIncompleteQuestions) {
    pendingSaveType.value = 'draft'
    showIncompleteModal.value = true
    return
  }

  // If all questions are complete, proceed directly
  await performSave('draft')
}

const proceedWithSave = async () => {
  showIncompleteModal.value = false
  await performSave(pendingSaveType.value)
}

const performSave = async (type: 'exam' | 'draft') => {
  try {
    loading.value = true
    const cleanedExamData = cleanExamData(newExam.value)
    cleanedExamData.exam_status = type === 'exam' ? 1 : 0
    await examService.createExam(cleanedExamData)
    showToast(`Exam ${type === 'exam' ? 'created' : 'saved as draft'} successfully!`, 'success')
    resetForm()
    emit('examCreated')
    emit('back')
  } catch (err: any) {
    console.error(`Error ${type === 'exam' ? 'creating' : 'saving'} exam:`, err)
    showToast(err.response?.data?.message || `Failed to ${type === 'exam' ? 'create' : 'save'} exam`, 'error')
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  newExam.value = {
    exam_name: '',
    module_id: 0,
    exam_content: {
      questions: []
    },
    answer_key: {},
    passing_rate: 70,
    time_limit: null,
    exam_status: 1
  }
  questionIdCounter = 1
}

// Lifecycle
onMounted(() => {
  fetchModules()
})
</script>

<style scoped>
.card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

.card-header {
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.dark .card-header {
  border-bottom-color: #374151;
}

.card-body {
  padding: 1.5rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s;
  border: 1px solid transparent;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-primary {
  background-color: var(--primary, #3b82f6);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-dark, #2563eb);
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #4b5563;
}

.btn-outline {
  background-color: transparent;
  color: var(--primary, #3b82f6);
  border-color: var(--primary, #3b82f6);
}

.btn-outline:hover:not(:disabled) {
  background-color: var(--primary, #3b82f6);
  color: white;
}

.dark .btn-outline {
  color: var(--primary-dark, #60a5fa);
  border-color: var(--primary-dark, #60a5fa);
}

.dark .btn-outline:hover:not(:disabled) {
  background-color: var(--primary-dark, #60a5fa);
  color: white;
}

/* Exam view container animation */
.exam-view-container {
  animation: slideInUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.sticky-sidebar {
  position: sticky;
  top: 4.5rem; /* offset from top to account for header/controls */
  display: block;
}

@media (max-width: 1279px) {
  /* On smaller screens the sidebar should not be sticky */
  .sticky-sidebar {
    position: static;
    top: auto;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stagger animation for child elements */
.exam-view-container > * {
  animation: fadeInStagger 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.exam-view-container > *:nth-child(1) { animation-delay: 0.1s; }
.exam-view-container > *:nth-child(2) { animation-delay: 0.15s; }
.exam-view-container > *:nth-child(3) { animation-delay: 0.2s; }
.exam-view-container > *:nth-child(4) { animation-delay: 0.25s; }

@keyframes fadeInStagger {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
